import api from '../axios-config';

export interface MediaCategory {
  mediaCategoryId: string;
  organizationId: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface MediaCategoryRequest {
  name: string;
  description?: string;
}

class MediaCategoriesService {
  /**
   * Get all media categories for an organization
   * Requires ManageMediaStorage claim
   */
  async getCategories(organizationId: string): Promise<MediaCategory[]> {
    try {
      const response = await api.get<MediaCategory[]>(
        `/api/MediaCategories/${organizationId}`
      );
      return response.data.map(item => ({
        mediaCategoryId: item.mediaCategoryId,
        organizationId: item.organizationId,
        name: item.name,
        description: item.description,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));
    } catch (error) {
      console.error('Error fetching media categories:', error);
      throw error;
    }
  }

  /**
   * Get a single media category by ID
   * Requires ManageMediaStorage claim
   */
  async getCategory(
    organizationId: string,
    categoryId: string
  ): Promise<MediaCategory> {
    try {
      const response = await api.get<MediaCategory>(
        `/api/MediaCategories/${organizationId}/${categoryId}`
      );
      return {
        mediaCategoryId: response.data.mediaCategoryId,
        organizationId: response.data.organizationId,
        name: response.data.name,
        description: response.data.description,
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt,
      };
    } catch (error) {
      console.error('Error fetching media category:', error);
      throw error;
    }
  }

  /**
   * Create a new media category
   * Requires ManageMediaStorage claim
   */
  async createCategory(
    organizationId: string,
    request: MediaCategoryRequest
  ): Promise<MediaCategory> {
    try {
      const response = await api.post<MediaCategory>(
        `/api/MediaCategories/${organizationId}`,
        request
      );
      return {
        mediaCategoryId: response.data.mediaCategoryId,
        organizationId: response.data.organizationId,
        name: response.data.name,
        description: response.data.description,
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt,
      };
    } catch (error) {
      console.error('Error creating media category:', error);
      throw error;
    }
  }

  /**
   * Update an existing media category
   * Requires ManageMediaStorage claim
   */
  async updateCategory(
    organizationId: string,
    categoryId: string,
    request: MediaCategoryRequest
  ): Promise<MediaCategory> {
    try {
      const response = await api.put<MediaCategory>(
        `/api/MediaCategories/${organizationId}/${categoryId}`,
        request
      );
      return {
        mediaCategoryId: response.data.mediaCategoryId,
        organizationId: response.data.organizationId,
        name: response.data.name,
        description: response.data.description,
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt,
      };
    } catch (error) {
      console.error('Error updating media category:', error);
      throw error;
    }
  }

  /**
   * Delete a media category
   * Requires ManageMediaStorage claim
   */
  async deleteCategory(
    organizationId: string,
    categoryId: string
  ): Promise<void> {
    try {
      await api.delete(
        `/api/MediaCategories/${organizationId}/${categoryId}`
      );
    } catch (error) {
      console.error('Error deleting media category:', error);
      throw error;
    }
  }
}

export default new MediaCategoriesService();